import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the spouse visa document checklist guide. */
const TITLE = "배우자 비자용 태국 서류 체크리스트 — 인증 순서와 반송 사유";
const DESC =
  "배우자·파트너 비자에 필요한 태국 서류, 번역과 인증의 올바른 순서, 그리고 불허나 반송으로 이어지는 대표적인 실수를 정리했습니다.";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "배우자 비자에 필요한 태국 서류는 무엇입니까?",
    answer:
      "혼인등록 증명, 가족관계·주소 관련 등록 서류, 신분증·여권, 범죄경력증명서, 그리고 관계와 재정을 증명하는 자료가 일반적입니다. 최종 목록은 제출 기관이 정합니다.",
  },
  {
    question: "번역은 어떤 언어로 해야 합니까?",
    answer:
      "제출 기관이 수리하는 언어에 맞춥니다. 영어를 요구하는 경우가 많지만, 현지 언어 번역을 요구하는 기관도 있습니다.",
  },
  {
    question: "인증 순서는 어떻게 됩니까?",
    answer:
      "원본 또는 인증 사본 확보 → 사문서는 공증 → 외교부 영사국 확인 → 번역 → 제출 대상국 주태 대사관·영사관 확인 순서가 일반적입니다.",
  },
  {
    question: "범죄경력증명서도 필요합니까?",
    answer:
      "많은 기관이 요구합니다. 발급 후 3~6개월 이내를 요구하는 경우가 있어 제출 시점을 기준으로 발급 시기를 조정해야 합니다.",
  },
  {
    question: "관계 증명 자료는 어느 정도 준비해야 합니까?",
    answer:
      "교류 기록, 동거 또는 방문 이력, 공동 재정 자료 등 시간 순서로 일관되게 정리합니다. 분량보다 일관성과 진정성이 중요합니다.",
  },
  {
    question: "배우자가 태국 밖에 있어도 진행할 수 있습니까?",
    answer:
      "가능합니다. 인증된 위임장으로 대리 진행하는 경우가 많으며, 위임 범위에 신청·수령·인증·제출을 모두 포함해야 합니다.",
  },
  {
    question: "반송·불허의 주된 사유는 무엇입니까?",
    answer:
      "인증 순서 착오, 이름 표기 불일치, 유효기간 초과, 도장 문구 번역 누락, 관계 증명 자료의 시간 순 불일치입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/spouse-visa-document-checklist")({
  component: KoSpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "배우자 비자 태국 서류, 태국 혼인증명서 번역, 태국 범죄경력증명서, 태국 영사국 인증, 배우자 비자 체크리스트",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: string[] = [
  "혼인등록 증명 (원본 또는 인증 사본)",
  "가족관계·주소 관련 등록 서류",
  "양측 여권 및 태국 신분증",
  "범죄경력증명서 (요구되는 경우, 발급 시기 확인)",
  "관계 증명 자료: 사진, 교류 기록, 방문·동거 이력",
  "재정 자료: 재직·소득·예금 관련 서류",
  "필요 시 인증된 위임장",
];

const STEPS: string[] = [
  "제출 기관의 서류 목록·언어·유효기간 요건을 서면으로 확인한다",
  "등록 서류의 원본 또는 인증 사본을 발급받는다",
  "사문서는 공증인의 서명 인증을 받는다",
  "외교부 영사국 확인을 받는다",
  "도장과 주석까지 포함해 요구 언어로 번역한다",
  "주태 대사관·영사관 확인을 받고 최종 점검 후 제출한다",
];

const MISTAKES: string[] = [
  "번역을 먼저 하고 인증을 나중에 진행했다",
  "여권·혼인증명서 간 이름 표기가 일치하지 않는다",
  "범죄경력증명서 발급이 오래되어 기간 요건에 미달한다",
  "인증 도장·스탬프 문구가 번역에 없다",
  "관계 증명 자료의 시간 순서가 서로 맞지 않는다",
  "위임 범위가 좁아 인증·제출 단계에서 절차가 멈췄다",
];

function KoSpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="ko" />
      <p className="answer-summary speakable-summary">
        배우자 비자 서류는 “원본·인증 사본 → 공증 → 영사국 → 번역 → 대사관” 순서로 준비하는 것이 안전합니다.
        불허와 반송은 대부분 순서 착오, 이름 표기 불일치, 유효기간 초과에서 발생하므로 제출 기한에서 거꾸로 계획하십시오.
      </p>

      <h2>1. 준비 서류</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2>2. 진행 순서</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 자주 나오는 반송 사유</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 참고 링크 및 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>{" "}
        ·{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          태국 내무부 지방행정국(등록업무)
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/visa" className="text-primary underline">
            비자·이민 서류 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/pcc" className="text-primary underline">
            범죄경력증명서 지원
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 확인일 {PUBLISHED}. 본 가이드는 준비를 위한 일반 정보이며 비자 승인을 보장하지 않습니다. 진행 전 제출 기관에서 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
